/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  GetlifeContext,
  LanguageT,
} from "../../../../../../context/GetLifeContext.context";
import { I18nContext } from "../../../../../../context/i18n.context";
import HttpCheckoutRepository from "../../../../../../api/request/Checkout/Checkout.service";
import HttpSignatureRepository from "../../../../../../api/request/Signature/Signature.service";
import useRecursiveTimeout from "../../../../../../hooks/useRecursiveTimeout";
import ErrorResponse from "../../../../../../api/core/ErrorResponse";
import { ResponseStatus } from "../../../../../../api/core/ResponseStatus";
import HttpDocumentsRepository from "../../../../../../api/request/Documents/Documents.service";
import { track, trackSubmissionError } from "../../../../../../helpers/Tracking";
import { isNativeMode } from "../../../../../../native/nativeMode";
import { nextSignatureUrlStep, RETRY_MS } from "./signatureUrlPolling";

type SignTextI = {
  [k in LanguageT]: { email: string; phone: string };
};

export const signText: SignTextI = {
  es: {
    email: "ayuda@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  fr: {
    email: "support@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  en: {
    email: "ayuda@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  pt: {
    email: "ayuda@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  it: {
    email: "supporto@life5.it",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  wl_es: {
    email: "ayuda@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
  sql_es: {
    email: "ayuda@life5.com",
    phone: process.env.REACT_APP_TEL_POST!,
  },
};

const CheckoutSignController = () => {
  const navigate = useNavigate();
  const [signedStatus, setSignedStatus] = useState(false);
  const [contractStatus, setContractStatus] = useState("");
  const { token, leadId, setLoading, domain, loading } =
    useContext(GetlifeContext);
  const {
    state: { translate },
  } = useContext(I18nContext);
  const [iframeUrl, setIframeUrl] = useState("");
  const [isSignaturit, setIsSignaturit] = useState<boolean>(false);

  const checkoutRepository = new HttpCheckoutRepository(leadId, token);
  const signatureRepository = new HttpSignatureRepository(leadId, token);

  // La firma se consulta en bucle: cada hito y el primer error se cuentan una vez.
  const signatureStartedRef = useRef(false);
  const signatureCompletedRef = useRef(false);
  const embedErrorReportedRef = useRef(false);
  const neglectedReportedRef = useRef(false);
  // La URL que enseña el iframe y si hay que dejar de preguntar (firmado o fuera de la página).
  const shownUrlRef = useRef("");
  const stoppedRef = useRef(false);

  const fetchCreateContract = async () => {
    try {
      await signatureRepository.postCreateContract();
      await recursiveRequestFunction();
    } catch (error) {
      if ((error as ErrorResponse).status === ResponseStatus.CONFLICT) {
        trackSubmissionError(error);
        navigate("/waitingsignature?signed=0");
      } else {
        trackSubmissionError(error, { error_type: "signature" });
      }
    }
  };

  const showUrl = (url: string) => {
    shownUrlRef.current = url;
    setIframeUrl(url);
    setIsSignaturit(url.includes("signaturit"));
  };

  const recursiveRequestFunction = async (): Promise<void> => {
    if (stoppedRef.current) {
      return;
    }
    let response;
    try {
      response = await signatureRepository.getSignatureEmbedded();
    } catch (error: any) {
      // Un error HTTP del proveedor de firma se cuenta una vez.
      if (error?.status && !embedErrorReportedRef.current) {
        embedErrorReportedRef.current = true;
        trackSubmissionError(error, { error_type: "signature" });
      }
      if (error?.status === 422) {
        navigate("/agent-confirmation");
        return;
      }
      setTimeout(recursiveRequestFunction, RETRY_MS);
      return;
    }

    // Sin URL todavía (el contrato se está generando) no es un error: se espera. Con la URL ya enseñada se sigue
    // mirando, porque el contrato puede regenerarse antes de firmar (ver signatureUrlPolling).
    const step = nextSignatureUrlStep(
      shownUrlRef.current,
      response,
      !isNativeMode()
    );
    if (step.kind === "show") {
      if (!signatureStartedRef.current) {
        signatureStartedRef.current = true;
        track("signature_shown", {});
      }
      showUrl(step.url);
    }
    if (step.kind === "wait" && step.clear) {
      // El sobre firmado tampoco está «pendiente»: antes de vaciar el iframe, se mira si acaba de firmar.
      await fetchSignStatus();
      if (stoppedRef.current) {
        return;
      }
      showUrl("");
    }
    if (step.nextCheckMs !== null) {
      setTimeout(recursiveRequestFunction, step.nextCheckMs);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchCreateContract();
    })();
    return () => {
      stoppedRef.current = true;
    };
  }, []);

  const fetchSignStatus = async () => {
    try {
      const res = await signatureRepository.getSignStatus();
      setContractStatus(res.status);
      if (res.status === "SIGNED") {
        stoppedRef.current = true;
        if (!signatureCompletedRef.current) {
          signatureCompletedRef.current = true;
          track("signature_completed", {});
        }
        setSignedStatus(true);
      }
      if (res.status === "NEGLECTED") {
        // La firma caduca: no es un error del envío.
        if (!neglectedReportedRef.current) {
          neglectedReportedRef.current = true;
          track("signature_expired", {});
        }
        navigate("/agent-confirmation");
      }
    } catch (error) {
      console.log("Error in apiGetSignStatus", error);
    }
  };

  const handleOnSignDelayedButtonClicked = () => {
    checkoutRepository.finishWaitingSignature().then(() => {
      navigate("/waitingsignature?signed=0");
    });
  };

  // Consulta de status cada 10 segundos.
  useRecursiveTimeout(async () => {
    await fetchSignStatus();
  }, 10000);

  useEffect(() => {
    if (signedStatus === true) {
      checkoutRepository.finishSigned().then(() => {
        navigate("/waitingsignature?signed=1");
      });
    }
  }, [leadId, token, signedStatus]);

  useEffect(() => {
    if (iframeUrl && loading) {
      setLoading(false);
    }
  }, [iframeUrl]);

  return {
    contractStatus,
    domain,
    handleOnSignDelayedButtonClicked,
    iframeUrl,
    isSignaturit,
    signText,
    translate,
  };
};

export default CheckoutSignController;
